import React, {
    FC, SyntheticEvent, useCallback, useState,
} from 'react';

import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import { PasswordVisibilityToggle, SpInput } from '@/components/Common/Sp';
import styles from './PasswordValidation.module.scss';

type ConditionTypes = 'length' | 'numbers' | 'uppercase' | 'lowercase' | 'chars';

const MAP_CONDITION_TO_REGEXP: Record<ConditionTypes, string> = {
    length: '(?=.{8,})',
    numbers: '(?=.*[0-9])',
    uppercase: '(?=.*[A-Z])',
    lowercase: '(?=.*[a-z])',
    chars: '(?=.*[!@#$%^&*()_~-])',
};

const NAME_SPACE = 'general.passwordValidation';

export interface PasswordValidationProps {
    updateInput: (password: string, isValid: boolean) => void;
    password: string;
}

const PasswordValidation: FC<PasswordValidationProps> = ({ updateInput, password }) => {
    const { t } = useTranslation();
    const [showPass, setShowPass] = useState<boolean>(false);

    const onPasswordChange = useCallback((nextPassword: string): void => {
        const isPasswordValid = Object.values(MAP_CONDITION_TO_REGEXP)
            .every((regex) => new RegExp(regex).test(nextPassword));
        updateInput(nextPassword, isPasswordValid);
    }, [updateInput]);

    const showHide = (event: SyntheticEvent): void => {
        event.preventDefault();
        event.stopPropagation();
        setShowPass((prev) => !prev);
    };

    const inputType = showPass ? 'text' : 'password';

    return (
        <div className={styles['password-check']}>
            <div className={styles['input-show-wrapper']}>
                <SpInput
                    type={inputType}
                    value={password}
                    placeholder={t(`${NAME_SPACE}.placeholder`)}
                    onChange={onPasswordChange}
                    required
                    autoComplete="new-password"
                />
                <PasswordVisibilityToggle className={styles.show} toggleVisibility={showHide} />
            </div>
            <ul className={styles['password-strength']}>
                {Object.entries(MAP_CONDITION_TO_REGEXP).map(([key, regex]) => (
                    <li
                        key={key}
                        className={classNames({
                            [styles['password-strength-good']]: new RegExp(regex).test(password),
                        })}
                    >
                        {t(`${NAME_SPACE}.${key}`)}
                    </li>
                ))}
            </ul>
        </div>
    );
};

export default PasswordValidation;
